// Screen wording rules — plan 9.1.3 "금칙어와 허용되는 꼴", ported from the mockup tools/wording-rules.mjs (same patterns).
// A rule = { name, re (global), ok?(before, word, after) → true when the context is an allowed form }.
// Context allowances (technical name in parentheses, role names, unit after a number, fixed negated phrases, statute
// quotes) are decided by `ok`; place allowances (command boxes, "기술 상세 보기" folds, glossary original terms, people's
// own text) are applied by the caller by leaving those strings out — see SKIP_WORDS for the on-screen selectors.

export interface WordingRule {
  name: string;
  re: RegExp;
  ok?: (before: string, word: string, after: string) => boolean;
}

export interface WordingHit {
  name: string;
  word: string;
  ctx: string;
}

// Evaluative words pass only inside a negated fixed phrase (plan 10.6.3: '근무·성과 평가에 쓰지 않아요', '이름순(순위 아님)')
const NEGATED = (_before: string, _word: string, after: string): boolean => /^[^.!?\n]{0,18}?(않|아니|아님|금지|없어|없이|말아|못 )/.test(after);
const NEGATED_EN = (before: string, _word: string, after: string): boolean =>
  /^[^.!?\n]{0,30}?\b(not|never|no|isn[’']t|aren[’']t|without)\b/i.test(after) ||
  /\b(not|never|no|without|isn[’']t|aren[’']t|don[’']t|doesn[’']t|won[’']t)\b[^.!?\n]*$/i.test(before);
// Technical name in parentheses after the plain word — '질문(프롬프트)', '한국 시간(KST)' (plan 9.1.1)
const IN_PARENS = (before: string, _word: string, after: string): boolean => /\([^()]*$/.test(before) && /^[^()]*\)/.test(after);

export const KO_RULES: readonly WordingRule[] = [
  { name: '세션', re: /세션/g, ok: IN_PARENS },
  { name: '작업 묶음', re: /작업 묶음/g },
  { name: '프롬프트', re: /프롬프트/g, ok: (b, w, a) => IN_PARENS(b, w, a) || /시스템 $/.test(b) },
  { name: '원문', re: /원문(?! 열람 담당)/g, ok: IN_PARENS },
  { name: '생성', re: /생성/g },
  { name: '추론 모델·강도', re: /추론 (모델|강도)/g, ok: IN_PARENS },
  { name: 'p50·p95', re: /(?<![가-힣a-zA-Z0-9_])p(50|95)(?![0-9])/g, ok: IN_PARENS },
  { name: '접근 파일', re: /접근 파일/g },
  { name: '사용 경로', re: /사용 경로/g },
  { name: '기록 범위', re: /기록 범위/g },
  { name: '시스템 상태', re: /시스템 상태/g },
  { name: '감사 기록', re: /감사 기록(?! 검토자)/g },
  { name: '감사 담당', re: /감사 담당/g },
  { name: 'null·N/A·미수집', re: /\bnull\b|\bundefined\b|\bNaN\b|\bN\/A\b|미수집/g },
  { name: '관측', re: /관측/g },
  { name: '메타데이터', re: /메타데이터/g },
  { name: '커버리지', re: /커버리지/g },
  { name: '로컬·대기열·큐·spool', re: /로컬|대기열|(?<![가-힣])큐(?![가-힣]{2})|spool/gi },
  { name: 'ACK', re: /\bACK\b/g },
  { name: '공급자·제공사·채널', re: /공급자|제공사|채널/g },
  { name: '보존 기간', re: /보존 기간/g, ok: (b) => /법[」』]?\s?(제\s?\d+조[^ ]*\s?)?(의|에 따른|상)?\s?$/.test(b) },
  { name: 'tokens·ms·KST', re: /\btokens\b|(?<![A-Za-z_])ms\b|\bKST\b|Asia\/Seoul/g, ok: IN_PARENS },
  { name: '환경변수·config.toml·exporter·Collector', re: /환경 ?변수|config\.toml|exporter|collector/gi },
  { name: '순위·성과(평가)', re: /(?<!우선)순위|랭킹|1등|우수|저조|생산성|(?<![가-힣])성과/g, ok: NEGATED },
];
/** Rule that a command-box comment line ('# …') may break (connection wizard command boxes). */
export const TECH_RULE = '환경변수·config.toml·exporter·Collector';

// English screens: the English forbidden words of 9.1.3 (tokens is the English unit, so it is allowed) and the English
// counterparts of the Korean rules that have their own label dictionary value.
export const EN_RULES: readonly WordingRule[] = [
  { name: 'session', re: /\bsessions?\b/gi, ok: (b, w, a) => IN_PARENS(b, w, a) || /\blogin $/i.test(b) },
  { name: 'p50·p95', re: /(?<![a-zA-Z0-9_])p(50|95)(?![0-9])/g, ok: IN_PARENS },
  { name: 'null·N/A', re: /\bnull\b|\bundefined\b|\bNaN\b|\bN\/A\b/g },
  { name: 'metadata', re: /\bmetadata\b/gi, ok: IN_PARENS },
  { name: 'spool·queue·ACK', re: /\bspool\b|\bqueue[ds]?\b|\bACK\b/gi, ok: IN_PARENS },
  { name: 'provider·channel', re: /\bproviders?\b|\bchannels?\b/gi },
  { name: 'ms·KST', re: /(?<![A-Za-z_])ms\b|\bKST\b|Asia\/Seoul/g, ok: IN_PARENS },
  { name: 'environment variable·config.toml·exporter·collector', re: /environment variables?|config\.toml|\bexporters?\b|\bcollectors?\b/gi },
  {
    name: 'rank·productivity·performance(평가)',
    re: /\brank(s|ed|ing|ings)?\b|\bleaderboard\b|\bproductivity\b|\bperformance\b|\btop performers?\b/gi,
    ok: NEGATED_EN,
  },
];
export const TECH_RULE_EN = 'environment variable·config.toml·exporter·collector';

/** Environment variable name shape (2+ upper-case words, e.g. OTEL_LOG_USER_PROMPTS) — only inside technical folds/command boxes. */
export const ENV_NAME = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g;

/** On-screen places excluded from the word check (technical folds, command boxes, diff, raw JSON, glossary original terms, people's text). */
export const TECH_FOLD = 'details.adm-tech, details.cn-tech';
export const SKIP_WORDS = [TECH_FOLD, '.cmd-box', '.code-box', 'pre', 'code', 'kbd', 'samp', '.diff-list', '.jt-wrap', '[data-term-source]', '.rich'].join(', ');
export const SKIP_ENV = [TECH_FOLD, '.cmd-box', '.code-box', 'pre', '.diff-list', '.jt-wrap', '[data-term-source]', '.rich'].join(', ');

const ctxOf = (text: string, i: number, len: number): string =>
  (text.slice(Math.max(0, i - 24), i) + '⟦' + text.slice(i, i + len) + '⟧' + text.slice(i + len, i + len + 28)).replace(/\s+/g, ' ').trim();

/** Places in `text` that break one of `rules`. */
export function findWords(text: string, rules: readonly WordingRule[]): WordingHit[] {
  const out: WordingHit[] = [];
  for (const r of rules) {
    r.re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = r.re.exec(text))) {
      const i = m.index;
      const w = m[0];
      const before = text.slice(Math.max(0, i - 48), i);
      const after = text.slice(i + w.length, i + w.length + 40);
      if (!r.ok || !r.ok(before, w, after)) out.push({ name: r.name, word: w, ctx: ctxOf(text, i, w.length) });
      if (!w.length) r.re.lastIndex++;
    }
  }
  return out;
}

export function findEnvNames(text: string): WordingHit[] {
  ENV_NAME.lastIndex = 0;
  const out: WordingHit[] = [];
  let m: RegExpExecArray | null;
  while ((m = ENV_NAME.exec(text))) out.push({ name: '환경 변수 이름', word: m[0], ctx: ctxOf(text, m.index, m[0].length) });
  return out;
}

const CODE_COMMENT = /^\s*(#|\/\/)\s/;

/**
 * Wording check for one dictionary value, as the mockup's check-static does it: Korean rules for ko, English rules for en,
 * and a command-box comment line ('# …') may name configuration files/settings.
 */
export function checkWording(text: string, lang: 'ko' | 'en'): WordingHit[] {
  return findWords(text, lang === 'en' ? EN_RULES : KO_RULES).filter(
    (f) => !((f.name === TECH_RULE || f.name === TECH_RULE_EN) && CODE_COMMENT.test(text)),
  );
}

/** Hangul left in an English value (the language button labels '한국어'·'한' are allowed). */
export function hasHangulInEnglish(text: string): boolean {
  return /[가-힣]/.test(text.replace(/한국어|한/g, ''));
}

/** Text without the given people's own strings (conversation titles, repository names …) — longest first. */
export function stripUserText(text: string, userStrings: Iterable<string>): string {
  let out = text;
  [...userStrings]
    .filter((x) => x && x.length > 2)
    .sort((a, b) => b.length - a.length)
    .forEach((u) => {
      out = out.split(u).join(' ');
    });
  return out;
}
